/**
 * Procurement & Inventory Service
 * Handles all procurement and inventory related API calls
 */

import { httpClient, ApiResponse } from './http.client';

// =================== INTERFACES ===================

export interface InventoryItem {
  item_id: string;
  organization_id: string;
  item_code: string;
  item_name: string;
  description?: string;
  category: 'lumber' | 'concrete' | 'steel' | 'electrical' | 'plumbing' | 'hvac' | 'roofing' | 'insulation' | 'drywall' | 'flooring' | 'hardware' | 'tools' | 'safety' | 'other';
  manufacturer?: string;
  model_number?: string;
  specifications?: any;
  unit_of_measure: 'each' | 'ft' | 'sq_ft' | 'cu_ft' | 'yd' | 'sq_yd' | 'cu_yd' | 'in' | 'lbs' | 'tons' | 'gal' | 'l' | 'cases' | 'boxes' | 'rolls' | 'sheets';
  quantity_on_hand: number;
  quantity_available: number;
  quantity_reserved: number;
  quantity_on_order: number;
  reorder_point?: number;
  min_stock_level?: number;
  max_stock_level?: number;
  unit_cost?: number;
  average_cost?: number;
  last_cost?: number;
  total_value?: number;
  warehouse_location?: string;
  bin_location?: string;
  status: 'in_stock' | 'low_stock' | 'out_of_stock' | 'on_order' | 'discontinued';
  is_active: boolean;
  is_trackable: boolean;
  preferred_vendor_id?: string;
  vendor_item_code?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface Vendor {
  vendor_id: string;
  organization_id: string;
  vendor_name: string;
  vendor_code?: string;
  vendor_type: 'supplier' | 'subcontractor' | 'service_provider' | 'distributor' | 'material_supplier' | 'equipment_rental' | 'consultant';
  contact_person?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  tax_id?: string;
  license_number?: string;
  insurance_info?: string;
  payment_terms?: string;
  credit_limit?: number;
  discount_percentage?: number;
  lead_time_days?: number;
  rating?: number;
  total_orders?: number;
  total_amount?: number;
  on_time_delivery_rate?: number;
  categories_supplied?: string[];
  specialties?: string;
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  is_preferred: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  storage_id?: string;
  file_id?: string;
  object_key?: string;
}

// =================== VENDOR CONTACTS ===================

export interface VendorContact {
  contact_id: string;
  vendor_id: string;
  contact_name: string;
  title?: string;
  department?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  is_primary: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface VendorContactCreate {
  contact_name: string;
  title?: string;
  department?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  is_primary?: boolean;
  notes?: string;
}

export interface VendorContactUpdate {
  contact_name?: string;
  title?: string;
  department?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  is_primary?: boolean;
  notes?: string;
}

// =================== VENDOR CONTRACTS ===================

export type ContractType = 'master_agreement' | 'purchase_agreement' | 'service_agreement' | 'rental_agreement' | 'nda' | 'other';
export type ContractStatus = 'draft' | 'active' | 'expired' | 'terminated' | 'pending_renewal';

export interface VendorContract {
  contract_id: string;
  vendor_id: string;
  contract_number: string;
  contract_name: string;
  contract_type: ContractType;
  status: ContractStatus;
  contract_value?: number;
  currency?: string;
  effective_date: string;
  expiry_date: string;
  renewal_date?: string;
  auto_renewal: boolean;
  description?: string;
  terms_conditions?: string;
  payment_terms?: string;
  document_id?: string;
  is_active: boolean;
  expires_soon: boolean;
  created_at: string;
  updated_at: string;
}

export interface VendorContractCreate {
  contract_number: string;
  contract_name: string;
  contract_type: ContractType;
  status?: ContractStatus;
  contract_value?: number;
  currency?: string;
  effective_date: string;
  expiry_date: string;
  renewal_date?: string;
  auto_renewal?: boolean;
  description?: string;
  terms_conditions?: string;
  payment_terms?: string;
  document_id?: string;
}

export interface VendorContractUpdate {
  contract_name?: string;
  contract_type?: ContractType;
  status?: ContractStatus;
  contract_value?: number;
  currency?: string;
  effective_date?: string;
  expiry_date?: string;
  renewal_date?: string;
  auto_renewal?: boolean;
  description?: string;
  terms_conditions?: string;
  payment_terms?: string;
  document_id?: string;
}

// =================== VENDOR EVALUATIONS ===================

export type PerformanceRating = 'excellent' | 'good' | 'satisfactory' | 'needs_improvement' | 'poor';

export interface VendorEvaluation {
  evaluation_id: string;
  vendor_id: string;
  project_id?: string;
  evaluation_period_start: string;
  evaluation_period_end: string;
  evaluation_type?: string;
  overall_rating: PerformanceRating;
  overall_score: number;
  quality_rating?: PerformanceRating;
  quality_score?: number;
  timeliness_rating?: PerformanceRating;
  timeliness_score?: number;
  communication_rating?: PerformanceRating;
  communication_score?: number;
  cost_effectiveness_rating?: PerformanceRating;
  cost_effectiveness_score?: number;
  safety_rating?: PerformanceRating;
  safety_score?: number;
  strengths?: string;
  areas_for_improvement?: string;
  specific_incidents?: string;
  recommendations?: string;
  on_time_delivery_percent?: number;
  budget_variance_percent?: number;
  defect_rate_percent?: number;
  customer_complaints?: number;
  action_items?: string[];
  follow_up_date?: string;
  follow_up_completed?: boolean;
  evaluated_by: string;
  evaluated_by_name?: string;
  evaluation_date: string;
  created_at: string;
}

export interface VendorEvaluationCreate {
  project_id?: string;
  evaluation_period_start: string;
  evaluation_period_end: string;
  evaluation_type?: string;
  overall_rating: PerformanceRating;
  overall_score: number;
  quality_rating?: PerformanceRating;
  quality_score?: number;
  timeliness_rating?: PerformanceRating;
  timeliness_score?: number;
  communication_rating?: PerformanceRating;
  communication_score?: number;
  cost_effectiveness_rating?: PerformanceRating;
  cost_effectiveness_score?: number;
  safety_rating?: PerformanceRating;
  safety_score?: number;
  strengths?: string;
  areas_for_improvement?: string;
  specific_incidents?: string;
  recommendations?: string;
  on_time_delivery_percent?: number;
  budget_variance_percent?: number;
  defect_rate_percent?: number;
  customer_complaints?: number;
  action_items?: string[];
  follow_up_date?: string;
  follow_up_completed?: boolean;
  evaluation_date: string;
}

// =================== VENDOR DOCUMENTS ===================

export type VendorDocumentType = 'insurance' | 'license' | 'certification' | 'contract' | 'w9' | 'reference' | 'other';

export interface VendorDocument {
  document_id: string;
  vendor_id: string;
  document_name: string;
  document_type: VendorDocumentType;
  document_number?: string;
  description?: string;
  file_id?: string;
  object_key?: string;
  file_name?: string;
  file_size?: number;
  mime_type?: string;
  issue_date?: string;
  expiry_date?: string;
  is_verified: boolean;
  verified_by?: string;
  verified_at?: string;
  verification_notes?: string;
  expires_soon: boolean;
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

export interface VendorDocumentCreate {
  document_name: string;
  document_type: VendorDocumentType;
  document_number?: string;
  description?: string;
  file_id?: string;
  object_key?: string;
  file_name?: string;
  file_size?: number;
  mime_type?: string;
  issue_date?: string;
  expiry_date?: string;
}

export interface VendorDocumentUpdate {
  document_name?: string;
  document_type?: VendorDocumentType;
  document_number?: string;
  description?: string;
  issue_date?: string;
  expiry_date?: string;
}

// =================== VENDOR PERFORMANCE METRICS ===================

export interface VendorPerformanceMetric {
  metric_id: string;
  vendor_id: string;
  metric_period: string;
  total_orders: number;
  total_spend: number;
  average_order_value: number;
  on_time_delivery_rate: number;
  quality_score: number;
  responsiveness_score: number;
  cost_competitiveness_score: number;
  overall_rating: number;
  calculated_at: string;
}

export interface PurchaseOrderItem {
  po_item_id: string;
  purchase_order_id: string;
  item_id?: string;
  item_code: string;
  item_name: string;
  description?: string;
  quantity_ordered: number;
  quantity_received: number;
  quantity_remaining: number;
  unit_of_measure: string;
  unit_price: number;
  total_amount: number;
  requested_delivery_date?: string;
  promised_delivery_date?: string;
  project_id?: string;
  cost_code_id?: string;
  created_at: string;
  updated_at: string;
}

export interface PurchaseOrder {
  po_id: string;
  purchase_order_id?: string; // Keep for backward compatibility
  organization_id: string;
  vendor_id: string;
  po_number: string;
  po_date: string;
  po_type?: string;
  description?: string;
  vendor_name: string;
  vendor_address?: string;
  vendor_contact?: string;
  requested_delivery_date?: string;
  required_date?: string;
  delivery_address?: string;
  shipping_method?: string;
  subtotal: number | string;
  tax_amount: number | string;
  shipping_amount: number | string;
  total_amount: number | string;
  payment_terms?: string;
  currency?: string;
  status: 'draft' | 'pending_approval' | 'approved' | 'sent' | 'acknowledged' | 'partially_received' | 'completed' | 'cancelled';
  created_by: string;
  created_by_name?: string;
  approved_by?: string;
  approved_at?: string;
  sent_to_vendor_at?: string;
  items: PurchaseOrderItem[];
  items_count?: number;
  pending_approval_count?: number;
  project_id?: string;
  project_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ReceivingItem {
  receiving_item_id: string;
  receiving_id: string;
  po_item_id?: string;
  item_id?: string;
  item_code: string;
  item_name: string;
  quantity_ordered: number;
  quantity_received: number;
  quantity_accepted: number;
  quantity_rejected: number;
  condition?: string;
  rejection_reason?: string;
  location_stored?: string;
  created_at: string;
}

export interface Receiving {
  receiving_id: string;
  organization_id: string;
  purchase_order_id?: string;
  receiving_number: string;
  received_date: string;
  vendor_id?: string;
  vendor_name: string;
  delivery_receipt_number?: string;
  carrier_name?: string;
  tracking_number?: string;
  received_by: string;
  inspected_by?: string;
  status: 'pending' | 'partial' | 'complete' | 'rejected';
  notes?: string;
  damage_notes?: string;
  items: ReceivingItem[];
  project_id?: string;
  created_at: string;
  updated_at: string;
}

export interface MaterialRequest {
  request_id: string;
  organization_id: string;
  project_id: string;
  request_number: string;
  request_date: string;
  required_by_date?: string;
  requested_by: string;
  department?: string;
  items: any[];
  delivery_location?: string;
  status: 'pending' | 'approved' | 'ordered' | 'received' | 'cancelled';
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
  estimated_cost?: number;
  actual_cost?: number;
  budget_code?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// =================== GOODS RECEIVED NOTES (RECEIVING ORDERS) ===================

export type ReceivingStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
export type QualityStatus = 'pending' | 'passed' | 'failed' | 'conditional' | 'rejected';
export type InspectionResult = 'not_required' | 'pending' | 'passed' | 'failed' | 'rework_required';
export type DiscrepancyType = 'quantity_shortage' | 'quantity_overage' | 'damage' | 'wrong_item' | 'quality_issue' | 'specification_mismatch' | 'missing_documentation';

export interface ReceivingOrderItem {
  receiving_item_id: string;
  receiving_order_id: string;
  organization_id?: string;
  line_number: number;
  po_item_id?: string;
  inventory_item_id?: string;
  item_code?: string;
  item_description: string;
  expected_quantity: number;
  received_quantity: number;
  accepted_quantity: number;
  rejected_quantity: number;
  unit_of_measure: string;
  unit_cost?: number;
  total_value?: number;
  inspection_result: InspectionResult;
  quality_notes?: string;
  warehouse_id?: string;
  location_id?: string;
  notes?: string;
  quantity_variance: number;
  variance_percentage: number;
  is_fully_received: boolean;
  item_name?: string;
  warehouse_name?: string;
  location_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ReceivingOrderItemCreate {
  line_number: number;
  po_item_id?: string;
  inventory_item_id?: string;
  item_code?: string;
  item_description: string;
  expected_quantity: number;
  unit_of_measure: string;
  unit_cost?: number;
  warehouse_id?: string;
  location_id?: string;
  notes?: string;
}

export interface ReceivingOrderItemUpdate {
  received_quantity?: number;
  accepted_quantity?: number;
  rejected_quantity?: number;
  inspection_result?: InspectionResult;
  quality_notes?: string;
  warehouse_id?: string;
  location_id?: string;
  notes?: string;
}

export interface ReceivingOrder {
  receiving_order_id: string;
  organization_id: string;
  receiving_number: string;
  status: ReceivingStatus;
  vendor_id: string;
  vendor_name?: string;
  purchase_order_id?: string;
  po_number?: string;
  expected_delivery_date?: string;
  actual_delivery_date?: string;
  delivery_location_id: string;
  location_name?: string;
  tracking_number?: string;
  carrier?: string;
  delivery_note_number?: string;
  packing_slip_number?: string;
  received_by?: string;
  received_by_name?: string;
  inspected_by?: string;
  inspected_by_name?: string;
  approved_by?: string;
  approved_by_name?: string;
  inspection_required: boolean;
  inspection_completed: boolean;
  quality_status: QualityStatus;
  delivery_documents?: any[];
  photos?: any[];
  special_instructions?: string;
  notes?: string;
  started_at?: string;
  completed_at?: string;
  is_completed: boolean;
  has_discrepancies: boolean;
  items_count: number;
  created_by: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ReceivingOrderWithItems extends ReceivingOrder {
  line_items: ReceivingOrderItem[];
}

export interface ReceivingOrderSummary {
  receiving_order_id: string;
  receiving_number: string;
  status: ReceivingStatus;
  vendor_id: string;
  vendor_name: string;
  purchase_order_id?: string;
  po_number?: string;
  expected_delivery_date?: string;
  actual_delivery_date?: string;
  quality_status: QualityStatus;
  items_count: number;
  has_discrepancies: boolean;
  location_name?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ReceivingOrderCreate {
  vendor_id: string;
  purchase_order_id?: string;
  expected_delivery_date?: string;
  delivery_location_id: string;
  tracking_number?: string;
  carrier?: string;
  delivery_note_number?: string;
  packing_slip_number?: string;
  inspection_required?: boolean;
  special_instructions?: string;
  notes?: string;
  line_items: ReceivingOrderItemCreate[];
}

export interface ReceivingOrderUpdate {
  vendor_id?: string;
  expected_delivery_date?: string;
  actual_delivery_date?: string;
  delivery_location_id?: string;
  tracking_number?: string;
  carrier?: string;
  delivery_note_number?: string;
  packing_slip_number?: string;
  received_by?: string;
  inspected_by?: string;
  approved_by?: string;
  inspection_required?: boolean;
  inspection_completed?: boolean;
  quality_status?: QualityStatus;
  special_instructions?: string;
  notes?: string;
}

export interface ReceivingDiscrepancy {
  discrepancy_id: string;
  receiving_order_id: string;
  receiving_item_id?: string;
  discrepancy_type: DiscrepancyType;
  severity: string;
  description: string;
  expected_quantity?: number;
  actual_quantity?: number;
  variance_quantity?: number;
  estimated_cost_impact?: number;
  resolution_required: boolean;
  resolution_description?: string;
  resolved: boolean;
  resolved_by?: string;
  resolved_at?: string;
  reported_by: string;
  assigned_to?: string;
  item_description?: string;
  reported_by_name?: string;
  assigned_to_name?: string;
  resolved_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ReceivingDiscrepancyCreate {
  receiving_item_id?: string;
  discrepancy_type: DiscrepancyType;
  severity?: string;
  description: string;
  expected_quantity?: number;
  actual_quantity?: number;
  variance_quantity?: number;
  estimated_cost_impact?: number;
  resolution_required?: boolean;
}

export interface ReceivingWorkflowAction {
  action: 'start' | 'complete' | 'hold' | 'cancel' | 'inspect' | 'approve';
  comments?: string;
  quality_status?: QualityStatus;
  photos?: any[];
}

// =================== PROCUREMENT ITEMS ===================

export type ItemCategory = 'DIVISION_01' | 'DIVISION_02' | 'DIVISION_03' | 'DIVISION_04' | 'DIVISION_05' |
  'DIVISION_06' | 'DIVISION_07' | 'DIVISION_08' | 'DIVISION_09' | 'DIVISION_10' | 'DIVISION_11' |
  'DIVISION_12' | 'DIVISION_13' | 'DIVISION_14' | 'DIVISION_21' | 'DIVISION_22' | 'DIVISION_23' |
  'DIVISION_26' | 'DIVISION_27' | 'DIVISION_28' | 'DIVISION_31' | 'DIVISION_32' | 'DIVISION_33';

export type UnitOfMeasure = 'CUBIC_YARD' | 'CUBIC_METER' | 'CUBIC_FOOT' | 'LITER' | 'GALLON' |
  'TON' | 'POUND' | 'KILOGRAM' | 'BAG' | 'FOOT' | 'METER' | 'INCH' | 'YARD' |
  'SQUARE_FOOT' | 'SQUARE_METER' | 'SQUARE_YARD' | 'PIECE' | 'EACH' |
  'BOX' | 'BUNDLE' | 'ROLL' | 'SHEET' | 'CARTON' | 'PALLET' | 'SET' | 'KIT' | 'LOT';

export interface ProcurementItem {
  item_id: string;
  organization_id: string;
  item_code: string;
  item_name: string;
  description?: string;
  category: ItemCategory;
  subcategory?: string;
  csi_code?: string;
  unit_of_measure: UnitOfMeasure;
  standard_unit_price?: number;
  manufacturer?: string;
  model_number?: string;
  specifications?: any;
  is_system_item: boolean;
  is_active: boolean;
  preferred_vendor_id?: string;
  reorder_level?: number;
  lead_time_days?: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ProcurementItemCreate {
  item_code: string;
  item_name: string;
  description?: string;
  category: ItemCategory;
  subcategory?: string;
  csi_code?: string;
  unit_of_measure: UnitOfMeasure;
  standard_unit_price?: number;
  manufacturer?: string;
  model_number?: string;
  specifications?: any;
  preferred_vendor_id?: string;
  reorder_level?: number;
  lead_time_days?: number;
}

export interface ProcurementItemUpdate {
  item_name?: string;
  description?: string;
  category?: ItemCategory;
  subcategory?: string;
  csi_code?: string;
  unit_of_measure?: UnitOfMeasure;
  standard_unit_price?: number;
  manufacturer?: string;
  model_number?: string;
  specifications?: any;
  is_active?: boolean;
  preferred_vendor_id?: string;
  reorder_level?: number;
  lead_time_days?: number;
}

// =================== BACKEND-MATCHING INVENTORY INTERFACES ===================

export interface InventoryItemBackend {
  inventory_item_id: string;
  organization_id: string;
  material_name: string;
  unit_of_measure: string;
  current_stock: number;
  min_stock_level: number | null;
  max_stock_level: number | null;
  unit_cost: number | null;
  warehouse_id: string | null;
  item_code: string | null;
  category: string | null;
  description: string | null;
  lead_time_days: number | null;
  is_active: boolean;
  last_updated_at: string;
}

export interface InventoryItemBackendCreate {
  organization_id: string;
  material_name: string;
  unit_of_measure: string;
  current_stock?: number;
  min_stock_level?: number;
  max_stock_level?: number;
  unit_cost?: number;
  warehouse_id?: string;
  item_code?: string;
  category?: string;
  description?: string;
  lead_time_days?: number;
}

export interface InventoryItemBackendUpdate {
  material_name?: string;
  unit_of_measure?: string;
  current_stock?: number;
  min_stock_level?: number;
  max_stock_level?: number;
  unit_cost?: number;
  item_code?: string;
  category?: string;
  description?: string;
  lead_time_days?: number;
  is_active?: boolean;
}

export type WarehouseType = 'central' | 'site_store' | 'transit';

export interface WarehouseBackend {
  warehouse_id: string;
  organization_id: string;
  warehouse_name: string;
  warehouse_type: WarehouseType;
  location: string | null;
  project_id: string | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  created_at: string;
}

export interface WarehouseCreate {
  organization_id: string;
  warehouse_name: string;
  warehouse_type: WarehouseType;
  location?: string;
  project_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface WarehouseUpdate {
  warehouse_name?: string;
  warehouse_type?: WarehouseType;
  location?: string;
  project_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

export type RequisitionStatus = 'Pending' | 'Approved' | 'Issued' | 'Rejected' | 'Cancelled';

export interface MaterialRequisitionBackend {
  requisition_id: string;
  organization_id: string;
  project_id: string;
  requisition_number: string;
  requested_by_user_id: string;
  approved_by_user_id: string | null;
  request_date: string | null;
  required_by_date: string | null;
  status: RequisitionStatus;
  notes: string | null;
  created_at: string;
}

export interface MaterialRequisitionCreate {
  organization_id: string;
  project_id: string;
  requisition_number: string;
  required_by_date?: string;
  delivery_warehouse_id?: string;
  delivery_address?: string;
  delivery_notes?: string;
  notes?: string;
}

export interface MaterialRequisitionItemBackend {
  req_item_id: string;
  organization_id: string;
  requisition_id: string;
  inventory_item_id: string;
  requested_quantity: number;
  issued_quantity: number;
  notes: string | null;
}

// =================== PROCUREMENT SERVICE CLASS ===================

class ProcurementService {
  // =================== INVENTORY ===================
  
  async getInventoryItems(page = 1, perPage = 20, filters?: any): Promise<ApiResponse<{ data: InventoryItem[]; total: number; page: number; per_page: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });
    
    return httpClient.get<{ data: InventoryItem[]; total: number; page: number; per_page: number }>(
      `/api/v1/inventory?${queryParams}`
    );
  }

  async getInventoryItem(itemId: string): Promise<ApiResponse<InventoryItem>> {
    return httpClient.get<InventoryItem>(`/api/v1/inventory/${itemId}`);
  }

  async createInventoryItem(itemData: Partial<InventoryItem>): Promise<ApiResponse<InventoryItem>> {
    return httpClient.post<InventoryItem>('/api/v1/inventory', itemData);
  }

  async updateInventoryItem(itemId: string, itemData: Partial<InventoryItem>): Promise<ApiResponse<InventoryItem>> {
    return httpClient.put<InventoryItem>(`/api/v1/inventory/${itemId}`, itemData);
  }

  async deleteInventoryItem(itemId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/inventory/${itemId}`);
  }

  async adjustInventoryQuantity(itemId: string, adjustment: { quantity: number; reason: string; reference?: string }): Promise<ApiResponse<InventoryItem>> {
    return httpClient.post<InventoryItem>(`/api/v1/inventory/${itemId}/adjust`, adjustment);
  }

  async getInventoryMovements(itemId: string, page = 1, perPage = 20): Promise<ApiResponse<{ data: any[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    return httpClient.get<{ data: any[]; total: number }>(
      `/api/v1/inventory/${itemId}/movements?${queryParams}`
    );
  }

  async getLowStockItems(): Promise<ApiResponse<{ data: InventoryItem[] }>> {
    return httpClient.get<{ data: InventoryItem[] }>('/api/v1/inventory/low-stock');
  }

  // =================== VENDORS ===================

  async getVendors(page = 1, perPage = 20, filters?: any): Promise<ApiResponse<{ data: Vendor[]; total: number; page: number; per_page: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });
    
    return httpClient.get<{ data: Vendor[]; total: number; page: number; per_page: number }>(
      `/api/v1/vendors?${queryParams}`
    );
  }

  async getVendor(vendorId: string): Promise<ApiResponse<Vendor>> {
    return httpClient.get<Vendor>(`/api/v1/vendors/${vendorId}`);
  }

  async createVendor(vendorData: Partial<Vendor>): Promise<ApiResponse<Vendor>> {
    return httpClient.post<Vendor>('/api/v1/vendors', vendorData);
  }

  async updateVendor(vendorId: string, vendorData: Partial<Vendor>): Promise<ApiResponse<Vendor>> {
    return httpClient.put<Vendor>(`/api/v1/vendors/${vendorId}`, vendorData);
  }

  async deleteVendor(vendorId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/vendors/${vendorId}`);
  }

  async getVendorPerformance(vendorId: string): Promise<ApiResponse<any>> {
    return httpClient.get<any>(`/api/v1/vendors/${vendorId}/performance`);
  }

  async approveVendor(vendorId: string): Promise<ApiResponse<Vendor>> {
    return httpClient.post<Vendor>(`/api/v1/vendors/${vendorId}/approve`, {});
  }

  async getVendorMetrics(vendorId: string, metricPeriod?: string): Promise<ApiResponse<any>> {
    const queryParams = metricPeriod ? `?metric_period=${metricPeriod}` : '';
    return httpClient.get<any>(`/api/v1/vendors/${vendorId}/metrics${queryParams}`);
  }

  async getVendorsExpiringSoon(organizationId: string, days: number = 30): Promise<ApiResponse<Vendor[]>> {
    const queryParams = new URLSearchParams({
      organization_id: organizationId,
      days: days.toString()
    });
    return httpClient.get<Vendor[]>(`/api/v1/vendors/expiring?${queryParams}`);
  }

  async getTopPerformingVendors(organizationId: string, metricPeriod?: string, limit: number = 5): Promise<ApiResponse<{ data: Vendor[] }>> {
    const queryParams = new URLSearchParams({
      organization_id: organizationId,
      limit: limit.toString()
    });
    if (metricPeriod) {
      queryParams.append('metric_period', metricPeriod);
    }
    return httpClient.get<{ data: Vendor[] }>(`/api/v1/vendors/top-performing?${queryParams}`);
  }

  // =================== VENDOR CONTACTS ===================

  async createVendorContact(vendorId: string, contactData: VendorContactCreate, organizationId?: string): Promise<ApiResponse<VendorContact>> {
    const payload = {
      ...contactData,
      vendor_id: vendorId,
      organization_id: organizationId
    };
    return httpClient.post<VendorContact>(`/api/v1/vendors/${vendorId}/contacts`, payload);
  }

  async getVendorContacts(vendorId: string): Promise<ApiResponse<{ data: VendorContact[] }>> {
    return httpClient.get<{ data: VendorContact[] }>(`/api/v1/vendors/${vendorId}/contacts`);
  }

  async getVendorContact(contactId: string): Promise<ApiResponse<VendorContact>> {
    return httpClient.get<VendorContact>(`/api/v1/vendors/contacts/${contactId}`);
  }

  async updateVendorContact(contactId: string, contactData: VendorContactUpdate): Promise<ApiResponse<VendorContact>> {
    return httpClient.put<VendorContact>(`/api/v1/vendors/contacts/${contactId}`, contactData);
  }

  async deleteVendorContact(contactId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/vendors/contacts/${contactId}`);
  }

  // =================== VENDOR CONTRACTS ===================

  async createVendorContract(vendorId: string, contractData: VendorContractCreate, organizationId?: string): Promise<ApiResponse<VendorContract>> {
    const payload = {
      ...contractData,
      vendor_id: vendorId,
      organization_id: organizationId
    };
    return httpClient.post<VendorContract>(`/api/v1/vendors/${vendorId}/contracts`, payload);
  }

  async getVendorContracts(vendorId: string, status?: string): Promise<ApiResponse<{ data: VendorContract[] }>> {
    const queryParams = status ? `?status=${status}` : '';
    return httpClient.get<{ data: VendorContract[] }>(`/api/v1/vendors/${vendorId}/contracts${queryParams}`);
  }

  async getVendorContract(contractId: string): Promise<ApiResponse<VendorContract>> {
    return httpClient.get<VendorContract>(`/api/v1/vendors/contracts/${contractId}`);
  }

  async updateVendorContract(contractId: string, contractData: VendorContractUpdate): Promise<ApiResponse<VendorContract>> {
    return httpClient.put<VendorContract>(`/api/v1/vendors/contracts/${contractId}`, contractData);
  }

  async getContractsExpiringSoon(organizationId: string, days: number = 60): Promise<ApiResponse<VendorContract[]>> {
    const queryParams = new URLSearchParams({
      organization_id: organizationId,
      days: days.toString()
    });
    return httpClient.get<VendorContract[]>(`/api/v1/vendors/contracts/expiring?${queryParams}`);
  }

  // =================== VENDOR EVALUATIONS ===================

  async createVendorEvaluation(vendorId: string, evaluationData: VendorEvaluationCreate, organizationId?: string): Promise<ApiResponse<VendorEvaluation>> {
    const payload = {
      ...evaluationData,
      vendor_id: vendorId,
      organization_id: organizationId
    };
    return httpClient.post<VendorEvaluation>(`/api/v1/vendors/${vendorId}/evaluations`, payload);
  }

  async getVendorEvaluations(vendorId: string, projectId?: string): Promise<ApiResponse<{ data: VendorEvaluation[] }>> {
    const queryParams = projectId ? `?project_id=${projectId}` : '';
    return httpClient.get<{ data: VendorEvaluation[] }>(`/api/v1/vendors/${vendorId}/evaluations${queryParams}`);
  }

  async getVendorEvaluation(evaluationId: string): Promise<ApiResponse<VendorEvaluation>> {
    return httpClient.get<VendorEvaluation>(`/api/v1/vendors/evaluations/${evaluationId}`);
  }

  // =================== VENDOR DOCUMENTS ===================

  async createVendorDocument(vendorId: string, documentData: VendorDocumentCreate): Promise<ApiResponse<VendorDocument>> {
    return httpClient.post<VendorDocument>(`/api/v1/vendors/${vendorId}/documents`, documentData);
  }

  async getVendorDocuments(vendorId: string, documentType?: string, activeOnly?: boolean): Promise<ApiResponse<{ data: VendorDocument[] }>> {
    const queryParams = new URLSearchParams();
    if (documentType) queryParams.append('document_type', documentType);
    if (activeOnly !== undefined) queryParams.append('active_only', activeOnly.toString());

    const queryString = queryParams.toString();
    return httpClient.get<{ data: VendorDocument[] }>(`/api/v1/vendors/${vendorId}/documents${queryString ? '?' + queryString : ''}`);
  }

  async getVendorDocument(documentId: string): Promise<ApiResponse<VendorDocument>> {
    return httpClient.get<VendorDocument>(`/api/v1/vendors/documents/${documentId}`);
  }

  async updateVendorDocument(documentId: string, documentData: VendorDocumentUpdate): Promise<ApiResponse<VendorDocument>> {
    return httpClient.put<VendorDocument>(`/api/v1/vendors/documents/${documentId}`, documentData);
  }

  async verifyVendorDocument(documentId: string, notes?: string): Promise<ApiResponse<VendorDocument>> {
    return httpClient.post<VendorDocument>(`/api/v1/vendors/documents/${documentId}/verify`, { notes });
  }

  async getDocumentsExpiringSoon(organizationId: string, days: number = 30): Promise<ApiResponse<VendorDocument[]>> {
    const queryParams = new URLSearchParams({
      organization_id: organizationId,
      days: days.toString()
    });
    return httpClient.get<VendorDocument[]>(`/api/v1/vendors/documents/expiring?${queryParams}`);
  }

  // =================== PROCUREMENT ITEMS ===================

  async searchProcurementItems(query: string, filters?: any): Promise<ApiResponse<{ data: ProcurementItem[] }>> {
    const queryParams = new URLSearchParams();

    // Add required parameters
    queryParams.append('query', query);

    // Add optional filters
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key] && key !== 'query') {
          queryParams.append(key, filters[key]);
        }
      });
    }

    const url = `/api/v1/procurement/items/search?${queryParams}`;
    const response = await httpClient.get<{ data: ProcurementItem[] }>(url);

    return response;
  }

  async getProcurementItems(page = 1, perPage = 20, filters?: any): Promise<ApiResponse<{ data: ProcurementItem[]; total: number; page: number; per_page: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });

    return httpClient.get<{ data: ProcurementItem[]; total: number; page: number; per_page: number }>(
      `/api/v1/procurement/items?${queryParams}`
    );
  }

  async getProcurementItem(itemId: string): Promise<ApiResponse<ProcurementItem>> {
    return httpClient.get<ProcurementItem>(`/api/v1/procurement/items/${itemId}`);
  }

  async createProcurementItem(itemData: ProcurementItemCreate): Promise<ApiResponse<ProcurementItem>> {
    return httpClient.post<ProcurementItem>('/api/v1/procurement/items', itemData);
  }

  async updateProcurementItem(itemId: string, itemData: ProcurementItemUpdate): Promise<ApiResponse<ProcurementItem>> {
    return httpClient.put<ProcurementItem>(`/api/v1/procurement/items/${itemId}`, itemData);
  }

  async deleteProcurementItem(itemId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/procurement/items/${itemId}`);
  }

  async getProcurementCategories(): Promise<ApiResponse<{ data: string[] }>> {
    return httpClient.get<{ data: string[] }>('/api/v1/procurement/items/categories');
  }

  // =================== PURCHASE ORDERS ===================

  async getPurchaseOrders(page = 1, perPage = 20, filters?: any): Promise<ApiResponse<{ data: PurchaseOrder[]; total: number; page: number; per_page: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });
    
    return httpClient.get<{ data: PurchaseOrder[]; total: number; page: number; per_page: number }>(
      `/api/v1/purchase-orders?${queryParams}`
    );
  }

  async getPurchaseOrder(poId: string): Promise<ApiResponse<PurchaseOrder>> {
    // Include items in the response
    return httpClient.get<PurchaseOrder>(`/api/v1/purchase-orders/${poId}?include_items=true`);
  }

  async getPurchaseOrderItems(poId: string): Promise<ApiResponse<PurchaseOrderItem[]>> {
    return httpClient.get<PurchaseOrderItem[]>(`/api/v1/purchase-orders/${poId}/items`);
  }

  async createPurchaseOrder(poData: any, organizationId?: string): Promise<ApiResponse<PurchaseOrder>> {
    const params = new URLSearchParams();
    const orgId = organizationId || poData.organization_id;
    if (orgId) {
      params.append('organization_id', orgId);
    }
    return httpClient.post<PurchaseOrder>(`/api/v1/purchase-orders?${params.toString()}`, poData);
  }

  async updatePurchaseOrder(poId: string, poData: Partial<PurchaseOrder>): Promise<ApiResponse<PurchaseOrder>> {
    return httpClient.put<PurchaseOrder>(`/api/v1/purchase-orders/${poId}`, poData);
  }

  async deletePurchaseOrder(poId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/purchase-orders/${poId}`);
  }

  async submitForApproval(poId: string): Promise<ApiResponse<PurchaseOrder>> {
    // Submit draft PO for approval (DRAFT -> PENDING_APPROVAL)
    return httpClient.post<PurchaseOrder>(`/api/v1/purchase-orders/${poId}/submit-for-approval`, {});
  }

  async getPendingApprovals(organizationId: string): Promise<ApiResponse<any[]>> {
    // Get pending approvals for organization
    return httpClient.get<any[]>(`/api/v1/purchase-orders/approvals/pending?organization_id=${organizationId}`);
  }

  async approvePurchaseOrder(approvalId: string, comments?: string): Promise<ApiResponse<PurchaseOrder>> {
    // Approve a pending approval (PENDING_APPROVAL -> APPROVED)
    return httpClient.post<PurchaseOrder>(`/api/v1/purchase-orders/approvals/${approvalId}/decide`, {
      action: 'approve',
      comments: comments || 'Approved for procurement'
    });
  }

  async rejectPurchaseOrder(approvalId: string, rejectionReason: string, comments?: string): Promise<ApiResponse<PurchaseOrder>> {
    // Reject a pending approval
    return httpClient.post<PurchaseOrder>(`/api/v1/purchase-orders/approvals/${approvalId}/decide`, {
      action: 'reject',
      rejection_reason: rejectionReason,
      comments
    });
  }

  async delegatePurchaseOrder(approvalId: string, delegateToId: string, comments?: string): Promise<ApiResponse<PurchaseOrder>> {
    // Delegate a pending approval to another user
    return httpClient.post<PurchaseOrder>(`/api/v1/purchase-orders/approvals/${approvalId}/decide`, {
      action: 'delegate',
      delegate_to_id: delegateToId,
      comments
    });
  }

  async sendPurchaseOrderToVendor(poId: string): Promise<ApiResponse<PurchaseOrder>> {
    return httpClient.post<PurchaseOrder>(`/api/v1/purchase-orders/${poId}/send-to-vendor`, {});
  }

  async acknowledgePurchaseOrder(poId: string): Promise<ApiResponse<PurchaseOrder>> {
    return httpClient.post<PurchaseOrder>(`/api/v1/purchase-orders/${poId}/acknowledge`, {});
  }

  async cancelPurchaseOrder(poId: string, comments?: string): Promise<ApiResponse<PurchaseOrder>> {
    // Cancel PO using workflow endpoint (only for draft or pending_approval)
    return httpClient.post<PurchaseOrder>(`/api/v1/purchase-orders/${poId}/workflow`, {
      action: 'cancel',
      comments
    });
  }

  async closePurchaseOrder(poId: string, comments?: string): Promise<ApiResponse<PurchaseOrder>> {
    // Close PO using workflow endpoint (only for received status)
    return httpClient.post<PurchaseOrder>(`/api/v1/purchase-orders/${poId}/workflow`, {
      action: 'close',
      comments
    });
  }

  async getNextPONumber(organizationId: string): Promise<ApiResponse<{ po_number: string }>> {
    return httpClient.get<{ po_number: string }>(`/api/v1/purchase-orders/next-po-number?organization_id=${organizationId}`);
  }

  // =================== RECEIVING ===================

  async getReceivingRecords(page = 1, perPage = 20, filters?: any): Promise<ApiResponse<{ data: Receiving[]; total: number; page: number; per_page: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });
    
    return httpClient.get<{ data: Receiving[]; total: number; page: number; per_page: number }>(
      `/api/v1/receiving?${queryParams}`
    );
  }

  async getReceivingRecord(receivingId: string): Promise<ApiResponse<Receiving>> {
    return httpClient.get<Receiving>(`/api/v1/receiving/${receivingId}`);
  }

  async createReceivingRecord(receivingData: Partial<Receiving>): Promise<ApiResponse<Receiving>> {
    return httpClient.post<Receiving>('/api/v1/receiving', receivingData);
  }

  async updateReceivingRecord(receivingId: string, receivingData: Partial<Receiving>): Promise<ApiResponse<Receiving>> {
    return httpClient.put<Receiving>(`/api/v1/receiving/${receivingId}`, receivingData);
  }

  async completeReceiving(receivingId: string): Promise<ApiResponse<Receiving>> {
    return httpClient.post<Receiving>(`/api/v1/receiving/${receivingId}/complete`, {});
  }

  // =================== MATERIAL REQUESTS ===================

  async getMaterialRequests(page = 1, perPage = 20, filters?: any): Promise<ApiResponse<{ data: MaterialRequest[]; total: number; page: number; per_page: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });
    
    return httpClient.get<{ data: MaterialRequest[]; total: number; page: number; per_page: number }>(
      `/api/v1/material-requests?${queryParams}`
    );
  }

  async getMaterialRequest(requestId: string): Promise<ApiResponse<MaterialRequest>> {
    return httpClient.get<MaterialRequest>(`/api/v1/material-requests/${requestId}`);
  }

  async createMaterialRequest(requestData: Partial<MaterialRequest>): Promise<ApiResponse<MaterialRequest>> {
    return httpClient.post<MaterialRequest>('/api/v1/material-requests', requestData);
  }

  async updateMaterialRequest(requestId: string, requestData: Partial<MaterialRequest>): Promise<ApiResponse<MaterialRequest>> {
    return httpClient.put<MaterialRequest>(`/api/v1/material-requests/${requestId}`, requestData);
  }

  async approveMaterialRequest(requestId: string, notes?: string): Promise<ApiResponse<MaterialRequest>> {
    return httpClient.post<MaterialRequest>(`/api/v1/material-requests/${requestId}/approve`, { notes });
  }

  async rejectMaterialRequest(requestId: string, reason: string): Promise<ApiResponse<MaterialRequest>> {
    return httpClient.post<MaterialRequest>(`/api/v1/material-requests/${requestId}/reject`, { reason });
  }

  async convertRequestToPurchaseOrder(requestId: string, vendorId: string): Promise<ApiResponse<PurchaseOrder>> {
    return httpClient.post<PurchaseOrder>(`/api/v1/material-requests/${requestId}/convert-to-po`, { vendor_id: vendorId });
  }

  // =================== REPORTING & ANALYTICS ===================

  async getInventoryReport(filters?: any): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams(filters);
    return httpClient.get<any>(`/api/v1/reports/inventory?${queryParams}`);
  }

  async getProcurementSummary(dateRange?: { start: string; end: string }): Promise<ApiResponse<any>> {
    const queryParams = dateRange ? new URLSearchParams({
      start_date: dateRange.start,
      end_date: dateRange.end
    }) : '';
    
    return httpClient.get<any>(`/api/v1/procurement/summary?${queryParams}`);
  }

  async getVendorSpendAnalysis(dateRange?: { start: string; end: string }): Promise<ApiResponse<any>> {
    const queryParams = dateRange ? new URLSearchParams({
      start_date: dateRange.start,
      end_date: dateRange.end
    }) : '';
    
    return httpClient.get<any>(`/api/v1/procurement/vendor-spend?${queryParams}`);
  }

  async getCostTrends(itemId?: string, timeframe = '12months'): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams({ timeframe });
    if (itemId) queryParams.append('item_id', itemId);
    
    return httpClient.get<any>(`/api/v1/procurement/cost-trends?${queryParams}`);
  }

  // =================== UTILITY METHODS ===================

  async searchItems(query: string, category?: string): Promise<ApiResponse<{ data: InventoryItem[] }>> {
    const queryParams = new URLSearchParams({ q: query });
    if (category) queryParams.append('category', category);
    
    return httpClient.get<{ data: InventoryItem[] }>(
      `/api/v1/inventory/search?${queryParams}`
    );
  }

  async searchVendors(query: string, type?: string): Promise<ApiResponse<{ data: Vendor[] }>> {
    const queryParams = new URLSearchParams({ q: query });
    if (type) queryParams.append('type', type);
    
    return httpClient.get<{ data: Vendor[] }>(
      `/api/v1/vendors/search?${queryParams}`
    );
  }

  async generateReorderReport(): Promise<ApiResponse<{ data: InventoryItem[] }>> {
    return httpClient.get<{ data: InventoryItem[] }>('/api/v1/inventory/reorder-report');
  }

  async getSupplierCatalog(vendorId: string): Promise<ApiResponse<{ data: any[] }>> {
    return httpClient.get<{ data: any[] }>(`/api/v1/vendors/${vendorId}/catalog`);
  }

  async bulkUpdatePrices(updates: { item_id: string; new_cost: number }[]): Promise<ApiResponse<any>> {
    return httpClient.post<any>('/api/v1/inventory/bulk-update-prices', { updates });
  }

  // =================== GOODS RECEIVED NOTES (RECEIVING ORDERS) ===================

  async getReceivingOrders(filters?: { status?: ReceivingStatus; vendor_id?: string; po_id?: string; skip?: number; limit?: number }): Promise<ApiResponse<ReceivingOrderSummary[]>> {
    const queryParams = new URLSearchParams();
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.vendor_id) queryParams.append('vendor_id', filters.vendor_id);
    if (filters?.po_id) queryParams.append('po_id', filters.po_id);
    if (filters?.skip !== undefined) queryParams.append('skip', filters.skip.toString());
    if (filters?.limit !== undefined) queryParams.append('limit', filters.limit.toString());
    return httpClient.get<ReceivingOrderSummary[]>(`/api/v1/operations/receiving-orders?${queryParams}`);
  }

  async getReceivingOrder(receivingId: string): Promise<ApiResponse<ReceivingOrderWithItems>> {
    return httpClient.get<ReceivingOrderWithItems>(`/api/v1/operations/receiving-orders/${receivingId}`);
  }

  async createReceivingOrder(data: ReceivingOrderCreate): Promise<ApiResponse<ReceivingOrder>> {
    return httpClient.post<ReceivingOrder>('/api/v1/operations/receiving-orders', data);
  }

  async updateReceivingOrder(receivingId: string, data: ReceivingOrderUpdate): Promise<ApiResponse<ReceivingOrder>> {
    return httpClient.put<ReceivingOrder>(`/api/v1/operations/receiving-orders/${receivingId}`, data);
  }

  async receivingWorkflowAction(receivingId: string, action: ReceivingWorkflowAction): Promise<ApiResponse<ReceivingOrder>> {
    return httpClient.post<ReceivingOrder>(`/api/v1/operations/receiving-orders/${receivingId}/workflow`, action);
  }

  async updateReceivingItem(itemId: string, data: ReceivingOrderItemUpdate): Promise<ApiResponse<ReceivingOrderItem>> {
    return httpClient.put<ReceivingOrderItem>(`/api/v1/operations/receiving-items/${itemId}`, data);
  }

  async createReceivingDiscrepancy(receivingId: string, data: ReceivingDiscrepancyCreate): Promise<ApiResponse<ReceivingDiscrepancy>> {
    return httpClient.post<ReceivingDiscrepancy>(`/api/v1/operations/receiving-orders/${receivingId}/discrepancies`, data);
  }

  async resolveReceivingDiscrepancy(discrepancyId: string, data: Partial<ReceivingDiscrepancy>): Promise<ApiResponse<ReceivingDiscrepancy>> {
    return httpClient.put<ReceivingDiscrepancy>(`/api/v1/operations/discrepancies/${discrepancyId}`, data);
  }

  async getReceivingAnalytics(receivingId: string): Promise<ApiResponse<any>> {
    return httpClient.get<any>(`/api/v1/operations/receiving-orders/${receivingId}/analytics`);
  }

  async getWarehouses(filters?: any): Promise<ApiResponse<{ data: any[] }>> {
    const queryParams = filters ? new URLSearchParams(filters) : '';
    return httpClient.get<{ data: any[] }>(`/api/v1/inventory/warehouses?${queryParams}`);
  }

  // =================== INVENTORY ITEMS (backend-matching API) ===================

  async getInventoryItemsBackend(organizationId: string, warehouseId?: string): Promise<InventoryItemBackend[]> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (warehouseId) params.set('warehouse_id', warehouseId);
    const res = await httpClient.get<InventoryItemBackend[]>(`/api/v1/inventory/inventory-items?${params}`);
    return (res as any).data ?? res;
  }

  async createInventoryItemBackend(data: InventoryItemBackendCreate): Promise<InventoryItemBackend> {
    const res = await httpClient.post<InventoryItemBackend>('/api/v1/inventory/inventory-items', data);
    return (res as any).data ?? res;
  }

  async updateInventoryItemBackend(itemId: string, data: InventoryItemBackendUpdate): Promise<InventoryItemBackend> {
    const res = await httpClient.put<InventoryItemBackend>(`/api/v1/inventory/inventory-items/${itemId}`, data);
    return (res as any).data ?? res;
  }

  async adjustInventoryStock(itemId: string, adjustmentQuantity: number, adjustmentReason: string): Promise<any> {
    const params = new URLSearchParams({
      adjustment_quantity: adjustmentQuantity.toString(),
      adjustment_reason: adjustmentReason,
    });
    const res = await httpClient.post<any>(`/api/v1/inventory/inventory-items/${itemId}/adjust-stock?${params}`, {});
    return (res as any).data ?? res;
  }

  async getWarehousesBackend(organizationId: string): Promise<WarehouseBackend[]> {
    const params = new URLSearchParams({ organization_id: organizationId });
    const res = await httpClient.get<WarehouseBackend[]>(`/api/v1/inventory/warehouses?${params}`);
    return Array.isArray((res as any).data) ? (res as any).data : Array.isArray(res) ? res : [];
  }

  async createWarehouse(data: WarehouseCreate): Promise<WarehouseBackend> {
    const res = await httpClient.post<WarehouseBackend>('/api/v1/inventory/warehouses', data);
    return (res as any).data ?? res;
  }

  async updateWarehouse(warehouseId: string, data: WarehouseUpdate): Promise<WarehouseBackend> {
    const res = await httpClient.put<WarehouseBackend>(`/api/v1/inventory/warehouses/${warehouseId}`, data);
    return (res as any).data ?? res;
  }

  async deactivateWarehouse(warehouseId: string): Promise<void> {
    await httpClient.post(`/api/v1/inventory/warehouses/${warehouseId}/deactivate`, {});
  }

  async getWarehouseEnums(): Promise<{ warehouse_types: WarehouseType[] }> {
    const res = await httpClient.get<{ warehouse_types: WarehouseType[] }>(
      '/api/v1/inventory/warehouses/enums',
    );
    return (res as any).data ?? res;
  }

  // =================== MATERIAL REQUISITIONS (backend API) ===================

  async getNextRequisitionNumber(organizationId: string): Promise<string> {
    const params = new URLSearchParams({ organization_id: organizationId });
    const res = await httpClient.get<{ requisition_number: string }>(`/api/v1/inventory/requisitions/next-number?${params}`);
    return ((res as any).data ?? res).requisition_number;
  }

  async getMaterialRequisitions(organizationId: string, projectId?: string, status?: string): Promise<MaterialRequisitionBackend[]> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (projectId) params.set('project_id', projectId);
    if (status) params.set('status', status);
    const res = await httpClient.get<MaterialRequisitionBackend[]>(`/api/v1/inventory/requisitions?${params}`);
    return Array.isArray((res as any).data) ? (res as any).data : Array.isArray(res) ? res : [];
  }

  async createMaterialRequisition(data: MaterialRequisitionCreate): Promise<MaterialRequisitionBackend> {
    const res = await httpClient.post<MaterialRequisitionBackend>('/api/v1/inventory/requisitions', data);
    return (res as any).data ?? res;
  }

  async approveMaterialRequisition(requisitionId: string): Promise<MaterialRequisitionBackend> {
    const res = await httpClient.post<MaterialRequisitionBackend>(`/api/v1/inventory/requisitions/${requisitionId}/approve`, {});
    return (res as any).data ?? res;
  }

  async rejectMaterialRequisition(requisitionId: string): Promise<MaterialRequisitionBackend> {
    const res = await httpClient.post<MaterialRequisitionBackend>(`/api/v1/inventory/requisitions/${requisitionId}/reject`, {});
    return (res as any).data ?? res;
  }

  async cancelMaterialRequisition(requisitionId: string): Promise<MaterialRequisitionBackend> {
    const res = await httpClient.post<MaterialRequisitionBackend>(`/api/v1/inventory/requisitions/${requisitionId}/cancel`, {});
    return (res as any).data ?? res;
  }

  async issueMaterialRequisition(requisitionId: string): Promise<MaterialRequisitionBackend> {
    const res = await httpClient.post<MaterialRequisitionBackend>(`/api/v1/inventory/requisitions/${requisitionId}/issue`, {});
    return (res as any).data ?? res;
  }

  async getRequisitionItems(requisitionId: string): Promise<MaterialRequisitionItemBackend[]> {
    const res = await httpClient.get<MaterialRequisitionItemBackend[]>(`/api/v1/inventory/requisitions/${requisitionId}/items`);
    return Array.isArray((res as any).data) ? (res as any).data : Array.isArray(res) ? res : [];
  }

  async addRequisitionItem(requisitionId: string, data: { organization_id: string; inventory_item_id: string; requested_quantity: number; notes?: string }): Promise<MaterialRequisitionItemBackend> {
    const res = await httpClient.post<MaterialRequisitionItemBackend>(`/api/v1/inventory/requisitions/${requisitionId}/items`, data);
    return (res as any).data ?? res;
  }
}

// Export singleton instance
export const procurementService = new ProcurementService();